import React, { useEffect, useMemo, useState } from "react";
import { CustomButton, SearchInput, SidePanel, SidePanelContent } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import styles from "./ProductTourAttributionSidePanel.module.css";
import { isEqual, isNonEmptyArray } from "../../../../../../common/utils";
import CompanyRow from "../CompanyRow";
import HeaderRow from "../HeaderRow";
import { useApi } from "../../../../../../common/hooks";
import productTourManagementService from "../../../../../../api/services/back-office/product-tour-management-service";

const dedupeById = (arr) => {
	const map = new Map();
	arr.forEach((item) => map.set(item.companyId, item));
	return Array.from(map.values());
};

export default function ProductTourAttributionSidePanel({
	open,
	selectedProductTours = [],
	onActive: onActiveFromParent = null,
	onClose,
	onSave,
}) {
	const [name, setName] = useState("");
	const [checkedCompanies, setCheckedCompanies] = useState([]);
	const [checkedAll, setCheckedAll] = useState(false);
	const [originalCompanies, setOriginalCompanies] = useState([]);
	const { call: getProductTourCompanies } = useApi(productTourManagementService.getProductTourCompanies);
	const { call: updateCompaniesForProductTour } = useApi(productTourManagementService.updateCompaniesForProductTour);
	const isThereAnyDifference = useMemo(
		() =>
			!isEqual(
				checkedCompanies.map((cmp) => cmp.companyId),
				originalCompanies.filter((cmp) => cmp.hasProductTour).map((cmp) => cmp.companyId)
			),
		[checkedCompanies, originalCompanies]
	);
	useEffect(() => {
		if (!open) {
			setCheckedCompanies([]);
			setCheckedAll(false);
			setName("");
		}
		if (open) {
			getProductTourCompanies({ productTourId: selectedProductTours[0]?.id })
				.then((data) => {
					setOriginalCompanies(data);
					setCheckedCompanies(data.filter((cp) => cp.hasProductTour));
					if (data.every((cp) => cp.hasProductTour)) {
						setCheckedAll(true);
					}
				})
				.catch(console.error);
		}
	}, [open, getProductTourCompanies, selectedProductTours]);
	const filteredCompanies = useMemo(
		() =>
			originalCompanies
				.filter((cp) => cp?.companyName?.toLowerCase()?.includes(name?.toLowerCase()))
				.sort((a, b) => a.companyName?.localeCompare(b.companyName)),
		[originalCompanies, name]
	);
	const checkedInCurrentFilteredCompanies = useMemo(
		() => checkedCompanies.filter((chkd) => filteredCompanies.some((fchkd) => fchkd.companyId === chkd.companyId)),
		[checkedCompanies, filteredCompanies]
	);
	const headerTitle = useMemo(
		() =>
			(selectedProductTours.length > 1 &&
				translate("dsi.product-tour-management.affectation.side-panel.title.plural")) ||
			translate("dsi.product-tour-management.affectation.side-panel.title.singular", {
				name: selectedProductTours[0]?.name,
			}),
		[selectedProductTours]
	);
	const handleChangeSearchValue = (e) => {
		setName(e.target.value);
	};
	const handleClearSearchValue = () => {
		setName("");
	};
	const handleCheckAll = () => {
		if (checkedInCurrentFilteredCompanies.length < filteredCompanies.length) {
			setCheckedAll(true);
			setCheckedCompanies((prev) => dedupeById([...prev, ...filteredCompanies]));
		} else {
			setCheckedAll(false);
			setCheckedCompanies((prev) =>
				prev.filter((chkd) => !filteredCompanies.some((fchkd) => fchkd.companyId === chkd.companyId))
			);
		}
	};
	const handleCheckCompany = (cmp) => {
		if (checkedInCurrentFilteredCompanies.some((cpt) => cpt.companyId === cmp.companyId)) {
			if (checkedInCurrentFilteredCompanies.length === 1) {
				setCheckedAll(false);
			}
			setCheckedCompanies((prev) => prev.filter((cpt) => cpt.companyId !== cmp.companyId));
		} else {
			if (checkedInCurrentFilteredCompanies.length === filteredCompanies.length - 1) {
				setCheckedAll(true);
			}
			setCheckedCompanies((prev) => [...prev, cmp]);
		}
	};
	const handleCancel = () => {
		setCheckedCompanies(originalCompanies.filter((cp) => cp.hasProductTour));
		setCheckedAll(false);
		handleClearSearchValue();
	};
	const handleSave = () => {
		const productTourId = selectedProductTours.length === 1 && selectedProductTours[0]?.id;
		if (productTourId) {
			updateCompaniesForProductTour(
				{ productTourId },
				checkedCompanies.map((cp) => cp.companyId)
			)
				.then(() => {
					onSave(productTourId, checkedCompanies.length);
					setOriginalCompanies((prev) =>
						prev.map((cp) => ({
							...cp,
							hasProductTour: checkedCompanies.some((chk) => chk.companyId === cp.companyId),
						}))
					);
				})
				.catch(console.error);
		}
	};
	return (
		<SidePanel disableBackdropClick open={open} size={65} onActive={onActiveFromParent} onClose={onClose}>
			{({ onActive }) => (
				<SidePanelContent title={headerTitle} onActive={onActive} onClose={onClose}>
					<div className={styles.container}>
						<div className={styles.containerHeader}>
							<CustomButton
								color="secondary"
								disabled={!isThereAnyDifference}
								variant="outlined"
								onClick={handleCancel}
							>
								{translate("common:btn.cancel")}
							</CustomButton>
							<CustomButton
								color="primary"
								disabled={!isThereAnyDifference}
								variant="contained"
								onClick={handleSave}
							>
								{translate("common:btn.save")}
							</CustomButton>
						</div>
						<div>
							<SearchInput
								fullWidth
								className={styles.searchContainer__textfield}
								label={translate("common:categories.textfield.label")}
								value={name}
								onChange={handleChangeSearchValue}
								onClearSearch={handleClearSearchValue}
							/>
						</div>
						<div className={styles.companyList}>
							<HeaderRow
								checkedAll={checkedAll}
								countChecked={checkedCompanies.length}
								currentCheckedList={checkedInCurrentFilteredCompanies}
								currentList={filteredCompanies}
								onCheckAll={handleCheckAll}
							/>
							<div>
								{isNonEmptyArray(filteredCompanies) &&
									filteredCompanies.map((cp) => (
										<CompanyRow
											key={cp.companyId}
											checked={checkedCompanies.some(
												(checkCp) => checkCp.companyId === cp.companyId
											)}
											company={cp}
											onCheck={handleCheckCompany}
										/>
									))}
							</div>
						</div>
					</div>
				</SidePanelContent>
			)}
		</SidePanel>
	);
}
